import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Stack, Typography } from '@mui/material';

import DomainBreadcrumbs from 'components/DomainBreadcrumbs';
import CustomButton from 'components/button/CustomButton';
import ConditionList from 'components/condition-list/ConditionList';
import ListEmptyState from 'components/condition-list/ListEmptyState';
import { ListPanelItem } from 'components/condition-list/ListPanel';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import {
  ExpenseLabel,
  ExpenseStore,
  createExpenseStore,
  deleteExpenseStore,
  listExpenseLabels,
  listExpenseStores,
  updateExpenseStore,
} from 'stores/ExpenseStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import ExpenseStoreForm, { ExpenseStoreFormValues } from './components/ExpenseStoreForm';

const NAME_MAX = 128;
const INVOICE_MAX = 14;

type Mode = 'none' | 'create' | 'detail' | 'edit';

const searchConditions: Condition[] = [
  { key: 'name', name: '店舗名', type: 'text', color: '#ef6c00' },
];

const emptyValues: ExpenseStoreFormValues = { name: '', defaultLabelId: '', invoiceNumber: '' };

/**
 * 店舗管理（パターンA・3ペイン）。
 * 左=店舗名 / 右=店舗名・デフォルト品目ラベル・インボイス番号編集。作成/更新/削除（論理削除・確認ダイアログ）。
 */
const ExpenseStoreManagement: React.FC = () => {
  const [stores, setStores] = React.useState<ExpenseStore[]>([]);
  const [labels, setLabels] = React.useState<ExpenseLabel[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [keyword, setKeyword] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string | undefined>();
  const [mode, setMode] = React.useState<Mode>('none');
  const [values, setValues] = React.useState<ExpenseStoreFormValues>(emptyValues);
  const [nameError, setNameError] = React.useState<string | undefined>();
  const [invoiceError, setInvoiceError] = React.useState<string | undefined>();
  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const fetchStores = React.useCallback(async () => {
    setLoading(true);
    try {
      const list = await listExpenseStores();
      setStores(list);
    } catch (e) {
      errorDefault('店舗の取得に失敗しました', toErrorDetail(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLabels = React.useCallback(async () => {
    try {
      const list = await listExpenseLabels();
      setLabels(list);
    } catch {
      // ラベル取得失敗はデフォルトラベル選択のみ影響。握りつぶす
    }
  }, []);

  React.useEffect(() => {
    fetchStores();
    fetchLabels();
  }, [fetchStores, fetchLabels]);

  const selected = React.useMemo(
    () => stores.find((s) => s.expenseStoreId === selectedId),
    [stores, selectedId],
  );

  const filtered = React.useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return stores;
    return stores.filter((s) => s.name.toLowerCase().includes(kw));
  }, [stores, keyword]);

  const items: ListPanelItem[] = React.useMemo(
    () =>
      filtered.map((s) => {
        const label = labels.find((l) => l.expenseLabelId === s.defaultLabelId);
        return {
          id: s.expenseStoreId,
          primary: s.name,
          secondary: label ? `既定: ${label.name}` : undefined,
        };
      }),
    [filtered, labels],
  );

  const handleSearch = (filters: Filter[]) => {
    const nameFilter = filters.find((f) => f.key === 'name');
    setKeyword(nameFilter?.value.key ?? '');
  };

  const toFormValues = (s: ExpenseStore): ExpenseStoreFormValues => ({
    name: s.name,
    defaultLabelId: s.defaultLabelId ?? '',
    invoiceNumber: s.invoiceNumber ?? '',
  });

  const handleSelect = (item: ListPanelItem) => {
    setSelectedId(item.id);
    setMode('detail');
    setNameError(undefined);
    setInvoiceError(undefined);
  };

  const handleCreateClick = () => {
    setSelectedId(undefined);
    setValues({ ...emptyValues });
    setNameError(undefined);
    setInvoiceError(undefined);
    setMode('create');
  };

  const handleEditClick = () => {
    if (!selected) return;
    setValues(toFormValues(selected));
    setNameError(undefined);
    setInvoiceError(undefined);
    setMode('edit');
  };

  const validate = (): boolean => {
    let ok = true;
    if (!values.name.trim()) {
      setNameError(MSG_REQUIRED('店舗名'));
      ok = false;
    } else if (values.name.length > NAME_MAX) {
      setNameError(MSG_MAX_LENGTH('店舗名', NAME_MAX));
      ok = false;
    } else {
      setNameError(undefined);
    }
    if (values.invoiceNumber && values.invoiceNumber.length > INVOICE_MAX) {
      setInvoiceError(MSG_MAX_LENGTH('インボイス番号', INVOICE_MAX));
      ok = false;
    } else {
      setInvoiceError(undefined);
    }
    return ok;
  };

  const buildParams = () => ({
    name: values.name.trim(),
    ...(values.defaultLabelId ? { defaultLabelId: values.defaultLabelId } : {}),
    ...(values.invoiceNumber.trim() ? { invoiceNumber: values.invoiceNumber.trim() } : {}),
  });

  /** 422（有効な同名重複）/400（defaultLabelId不正）をフォームにインライン表示。それ以外は委譲。 */
  const handleSaveError = (e: unknown, fallbackMsg: string) => {
    const status = (e as { status?: number })?.status;
    if (status === 422) {
      setNameError('同じ名前の店舗が既に存在します');
      return;
    }
    if (status === 400) {
      errorDefault('デフォルト品目ラベルが不正です。品目ラベルを再取得します', toErrorDetail(e));
      fetchLabels();
      return;
    }
    if (status === 403 || status === 404) {
      errorDefault('対象の店舗が見つかりません', toErrorDetail(e));
      fetchStores();
      setMode('none');
      setSelectedId(undefined);
      return;
    }
    errorDefault(fallbackMsg, toErrorDetail(e));
  };

  const handleCreate = async () => {
    if (!validate()) return;
    try {
      const id = await createExpenseStore(buildParams());
      successDefault('店舗を作成しました');
      await fetchStores();
      setSelectedId(id);
      setMode('detail');
    } catch (e) {
      handleSaveError(e, '店舗の作成に失敗しました');
    }
  };

  const handleUpdate = async () => {
    if (!selected || !validate()) return;
    try {
      await updateExpenseStore({ expenseStoreId: selected.expenseStoreId, ...buildParams() });
      successDefault('店舗を更新しました');
      await fetchStores();
      setMode('detail');
    } catch (e) {
      handleSaveError(e, '店舗の更新に失敗しました');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteExpenseStore({ expenseStoreId: selected.expenseStoreId });
      successDefault('店舗を削除しました');
      setDeleteOpen(false);
      setSelectedId(undefined);
      setMode('none');
      await fetchStores();
    } catch (e) {
      setDeleteOpen(false);
      const status = (e as { status?: number })?.status;
      if (status === 403 || status === 404) {
        errorDefault('対象の店舗が見つかりません', toErrorDetail(e));
        fetchStores();
        setSelectedId(undefined);
        setMode('none');
        return;
      }
      errorDefault('店舗の削除に失敗しました', toErrorDetail(e));
    }
  };

  const detailFooter =
    mode === 'create' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setMode('none')}
        />
        <CustomButton
          label="作成"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={handleCreate}
          disabled={values.name.trim() === ''}
        />
      </Stack>
    ) : mode === 'edit' ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="キャンセル"
          buttonVariant="secondary"
          onClick={() => setMode('detail')}
        />
        <CustomButton
          label="更新"
          buttonVariant="primary"
          icon={<SaveIcon />}
          onClick={handleUpdate}
          disabled={values.name.trim() === ''}
        />
      </Stack>
    ) : mode === 'detail' && selected ? (
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          label="削除"
          buttonVariant="danger"
          icon={<DeleteIcon />}
          onClick={() => setDeleteOpen(true)}
        />
        <CustomButton
          label="編集"
          buttonVariant="primary"
          icon={<EditIcon />}
          onClick={handleEditClick}
        />
      </Stack>
    ) : undefined;

  const detailContent =
    mode === 'create' || mode === 'edit' ? (
      <ExpenseStoreForm
        values={values}
        onChange={setValues}
        labels={labels}
        nameError={nameError}
        invoiceError={invoiceError}
      />
    ) : mode === 'detail' && selected ? (
      <ExpenseStoreForm
        values={toFormValues(selected)}
        onChange={() => {}}
        labels={labels}
        disabled
      />
    ) : null;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 0.5 }}>
      <DomainBreadcrumbs items={[{ label: '経費管理', to: '/expense' }, { label: '店舗管理' }]} />
      <ConditionList
        conditions={searchConditions}
        items={items}
        selectedId={selectedId}
        onSelect={handleSelect}
        onSearch={handleSearch}
        onCreateClick={handleCreateClick}
        createButtonLabel="店舗を作成"
        isProgress={loading}
        detailFooter={detailFooter}
        detailIsEmpty={mode === 'none'}
        detailEmptyState={
          <ListEmptyState
            title="店舗を選択してください"
            description="左のリストから選ぶか、＋から新規作成できます"
          />
        }
        listEmptyState={
          <ListEmptyState title="店舗がありません" description="＋から店舗を作成してください" />
        }
      >
        {detailContent}
      </ConditionList>

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
        title="店舗の削除"
        confirmLabel="削除"
      >
        <Typography variant="body2">
          「{selected?.name}
          」を削除します。既に登録済みの経費の表示には残りますが、今後の選択肢からは除外されます。
        </Typography>
      </DeleteConfirmDialog>
    </Box>
  );
};

export default ExpenseStoreManagement;
